// dependencies
import { format, isValid, parseISO } from "date-fns";
import React from "react";

import { ICustomQuestionGetResponse } from "@performyard/models";

// components
import Avatar from "../../../../../../components/Avatar";
import Chip from "../../../../../../components/Chip";
import Link from "../../../../../../components/Link";
import OptionsDrawer from "../../../../../../components/OptionsDrawer";
import Typography from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";

//service utils
import { scaleTypeMap } from "../../../../../../api/services";

// utils
import EmployeeURL from "../../../../../../utils/URLs/Employee";

//css
import css from "../SurveyQuestions.module.scss";

const getSurveyQuestionsTableCols = (
	handleOptionsDrawer: (option: { id: string; label: string }, _id: ICustomQuestionGetResponse) => void
) => {
	const columns = [
		{
			id: "factor",
			label: "Factor",
			width: "10%",
			isSortable: true,
			getter: (row: ICustomQuestionGetResponse) => {
				return (
					<Typography size="paragraph-3" weight="regular" testID="factor-row">
						{row?.factor || ""}
					</Typography>
				);
			},
		},
		{
			id: "question",
			label: "Question",
			width: "30%",
			isSortable: true,
			getter: (row: ICustomQuestionGetResponse) => (
				<div className={css.questionContainer}>
					<Typography size="paragraph-3" weight="regular" testID="question-row">
						<div className="break-all py-line-clamp-3">{row?.question || ""}</div>
					</Typography>
				</div>
			),
		},
		{
			id: "type",
			label: "Type",
			width: "10%",
			isSortable: true,
			getter: (row: ICustomQuestionGetResponse) => {
				const { scale = "" } = row;

				return (
					<Typography size="paragraph-3" weight="regular" testID="type-row">
						{scaleTypeMap[scale] || "N/A"}
					</Typography>
				);
			},
		},
		{
			id: "created_at",
			label: "Date Created",
			width: "10%",
			isSortable: true,
			getter: (row: ICustomQuestionGetResponse) => {
				const { created_at = "" } = row;
				const parsedDate = parseISO(created_at);
				const formattedDate = isValid(parsedDate) ? format(parsedDate, "MMM dd, yyyy") : "";

				return (
					<Typography size="paragraph-3" weight="regular">
						{formattedDate || "-"}
					</Typography>
				);
			},
		},
		{
			id: "updated_at",
			label: "Last Modified",
			width: "10%",
			isSortable: true,
			getter: (row: ICustomQuestionGetResponse) => {
				const { updated_at = "" } = row;
				const parsedDate = parseISO(updated_at);
				const formattedDate = isValid(parsedDate) ? format(parsedDate, "MMM dd, yyyy") : "";

				return (
					<Typography size="paragraph-3" weight="regular">
						{formattedDate || "-"}
					</Typography>
				);
			},
		},
		{
			id: "updated_by_employee.name",
			label: "Modified by",
			width: "14%",
			isSortable: true,
			getter: (row: ICustomQuestionGetResponse) => {
				const { updated_by_employee } = row;

				if (updated_by_employee) {
					return (
						<Link href={EmployeeURL(updated_by_employee?._id)} className={css.userBox}>
							<Avatar size={20} id={String(updated_by_employee?._id)} />
							<TruncatedText
								text={updated_by_employee?.name || ""}
								size="paragraph-3"
								weight="regular"
								maxWidth={110}
								useTooltip="never"
							/>
						</Link>
					);
				}
				return (
					<Typography size="paragraph-3" weight="regular">
						-
					</Typography>
				);
			},
		},
		{
			id: "enabled",
			label: "Status",
			width: "8%",
			isSortable: true,
			getter: (row: ICustomQuestionGetResponse) => {
				const { enabled = false } = row;
				return (
					<div>
						<Chip size="small" label={enabled ? "Enabled" : "Disabled"} color={enabled ? "green" : "red"} />
					</div>
				);
			},
		},
		{
			id: "options",
			label: "",
			width: "2%",
			isSortable: false,
			getter: (row: ICustomQuestionGetResponse) => {
				const { enabled, factor } = row;
				let options: { id: string; label: string }[] = [];

				// custom questions can be edited, disabled, or deleted
				if (factor === "Custom") {
					options = [
						{ id: "edit", label: "Edit" },
						enabled ? { id: "disable", label: "Disable" } : { id: "enable", label: "Enable" },
						{ id: "delete", label: "Delete" },
					] as { id: string; label: string }[];
				}

				// built in questions can only be enabled or disabled
				else {
					options = [enabled ? { id: "disable", label: "Disable" } : { id: "enable", label: "Enable" }] as {
						id: string;
						label: string;
					}[];
				}
				return (
					<OptionsDrawer
						openOn={"bottom"}
						horizontal={true}
						onSelect={(option) => handleOptionsDrawer(option, row)}
						options={options}
					/>
				);
			},
		},
	];
	return columns;
};

export default getSurveyQuestionsTableCols;
